import { faUsersClass } from '@fortawesome/pro-solid-svg-icons'
import type { ReactElement } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import type { RegistrationCardSessionRegistrationFragment } from '@/client/services/hasura/generated/graphql'

import UserCard from '@/components/Series/Banners/BreakoutSessionBanner/internal/UserCard'
import { useToaster } from '@/components/Toast/useToaster'
import type { MenuItemType } from '@/components/Worlds/UI/Menu/types'

export default function RegistrationCard({
  breakoutSessionIds,
  registration,
}: {
  breakoutSessionIds: number[]
  registration: RegistrationCardSessionRegistrationFragment
}): ReactElement {
  const { user } = registration
  const toast = useToaster()

  const [moveToBreakoutRoom, movingToBreakoutRoom] = useAPIWrapper(
    async (breakoutSessionId: number) => {
      const roomIndex = breakoutSessionIds.indexOf(breakoutSessionId)
      const roomNumber = roomIndex !== -1 ? roomIndex + 1 : '?'

      toast({
        title: `Moving ${user.virtual_display_name} to Room ${roomNumber}...`,
        status: 'info',
      })

      await RPC.registerUsersForBreakoutSession({
        userIds: [user.id],
        sessionBreakoutId: breakoutSessionId,
      })

      toast({
        title: `${user.virtual_display_name} moved to Room ${roomNumber} successfully`,
        status: 'success',
      })
    },
    [user.id, user.virtual_display_name, breakoutSessionIds, toast],
    'Move User to Breakout Room'
  )

  const [moveToUnassigned, movingToUnassigned] = useAPIWrapper(
    async () => {
      if (registration.session_breakout_id === null) {
        return // User is already unassigned
      }

      toast({
        title: `Moving ${user.virtual_display_name} to unassigned...`,
        status: 'info',
      })

      await RPC.unregisterUserFromBreakoutSession({
        userId: user.id,
        sessionBreakoutId: registration.session_breakout_id,
      })

      toast({
        title: `${user.virtual_display_name} moved to unassigned successfully`,
        status: 'success',
      })
    },
    [user.id, registration.session_breakout_id, user.virtual_display_name, toast],
    'Move User to Unassigned'
  )

  const menuItems: MenuItemType[][] = [
    [
      {
        label: 'Move to Unassigned',
        icon: faUsersClass,
        onClick: () => moveToUnassigned(),
        disabled: registration.session_breakout_id === null || movingToUnassigned,
      },
      ...breakoutSessionIds.map((breakoutId, index) => ({
        label: `Move to Room ${index + 1}`,
        icon: faUsersClass,
        onClick: () => moveToBreakoutRoom(breakoutId),
        disabled: registration.session_breakout_id === breakoutId || movingToBreakoutRoom,
      })),
    ],
  ]

  return (
    <UserCard user={user} attemptedJoining={registration.attempted_joining} menuItems={menuItems} />
  )
}
